import type {
    Candle,
    ExportEventRef,
    ExportMarket,
    ExportSettings,
    ExportSide,
    RawRow,
} from './exportTypes';
import type { TimeWindow } from './exportPlan';

const SIDE_FIELDS = [
    'volume_quote',
    'volume_base',
    'buy_volume',
    'sell_volume',
    'buy_count',
    'sell_count',
] as const;

const PRICE_FIELDS = ['open', 'high', 'low', 'close'] as const;

const INTERVAL_TOKENS: Readonly<Record<number, string>> = {
    1: '1m',
    5: '5m',
    15: '15m',
    30: '30m',
    60: '1h',
    120: '2h',
    240: '4h',
    360: '6h',
    720: '12h',
    1440: '1d',
    10080: '1w',
    43200: '1mo',
};

export function fieldsFor(side: ExportSide): string[] {
    if (side !== 'merged') return [...SIDE_FIELDS];
    return ['yes', 'no'].flatMap(name => SIDE_FIELDS.map(f => `${name}_${f}`));
}

function num(value: unknown): number {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : 0;
}

export function parseRows(
    rows: readonly RawRow[],
    fields: readonly string[]
): Candle[] {
    const candles: Candle[] = [];
    for (const row of rows) {
        const t = new Date(String(row.date ?? '')).getTime();
        if (!Number.isFinite(t)) continue;
        candles.push({
            t,
            open: num(row.open),
            high: num(row.high),
            low: num(row.low),
            close: num(row.close),
            values: fields.map(f => num(row[f])),
        });
    }
    return candles;
}

export function mergeCandles(pages: readonly (readonly Candle[])[]): Candle[] {
    const sorted = pages.flat().sort((a, b) => a.t - b.t);
    const merged: Candle[] = [];
    for (const candle of sorted) {
        const last = merged[merged.length - 1];
        if (!last || last.t !== candle.t) {
            merged.push({ ...candle, values: [...candle.values] });
            continue;
        }
        last.high = Math.max(last.high, candle.high);
        last.low = Math.min(last.low, candle.low);
        last.close = candle.close;
        last.values = last.values.map((v, i) => v + (candle.values[i] ?? 0));
    }
    return merged;
}

export function fillCandles(
    candles: readonly Candle[],
    window: TimeWindow,
    intervalMinutes: number
): Candle[] {
    if (candles.length === 0) return [];
    const step = intervalMinutes * 60_000;
    const filled: Candle[] = [];
    let index = 0;
    let lastClose = candles[0].close;
    const width = candles[0].values.length;
    for (let t = candles[0].t; t < window.endMs; t += step) {
        while (index < candles.length && candles[index].t < t) index += 1;
        const candle = candles[index];
        if (candle && candle.t === t) {
            filled.push(candle);
            lastClose = candle.close;
            index += 1;
        } else {
            filled.push({
                t,
                open: lastClose,
                high: lastClose,
                low: lastClose,
                close: lastClose,
                values: new Array<number>(width).fill(0),
            });
        }
    }
    return filled;
}

export function isoTime(ms: number): string {
    return new Date(ms).toISOString().replace(/\.\d+Z$/, 'Z');
}

function price(value: number): string {
    return String(Number(value.toFixed(6)));
}

export function candlesCsv(
    candles: readonly Candle[],
    fields: readonly string[]
): string {
    const lines = [['timestamp', ...PRICE_FIELDS, ...fields].join(',')];
    for (const c of candles) {
        lines.push(
            [
                isoTime(c.t),
                price(c.open),
                price(c.high),
                price(c.low),
                price(c.close),
                ...c.values.map(String),
            ].join(',')
        );
    }
    return lines.join('\n') + '\n';
}

function headerOf(settings: ExportSettings, window: TimeWindow) {
    return {
        event_id: settings.eventId,
        event_slug: settings.eventSlug,
        event_title: settings.eventTitle,
        side: settings.side,
        interval_minutes: settings.intervalMinutes,
        from: isoTime(window.startMs),
        to: isoTime(window.endMs),
        filled: settings.fill,
    };
}

export function candlesJson(
    settings: ExportSettings,
    window: TimeWindow,
    market: ExportMarket,
    candles: readonly Candle[],
    fields: readonly string[]
): string {
    return JSON.stringify({
        ...headerOf(settings, window),
        condition_id: market.conditionId,
        outcome: market.label,
        candles: candles.map(c => {
            const row: Record<string, string | number> = {
                timestamp: isoTime(c.t),
                open: c.open,
                high: c.high,
                low: c.low,
                close: c.close,
            };
            fields.forEach((f, i) => {
                row[f] = c.values[i] ?? 0;
            });
            return row;
        }),
    });
}

export function slugify(text: string): string {
    return (
        text
            .normalize('NFKD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 60) || 'outcome'
    );
}

export function columnNames(markets: readonly ExportMarket[]): string[] {
    const seen = new Map<string, number>();
    return markets.map(m => {
        const base = slugify(m.label).replace(/-/g, '_');
        const count = seen.get(base) ?? 0;
        seen.set(base, count + 1);
        return count === 0 ? base : `${base}_${m.conditionId.slice(0, 8)}`;
    });
}

export interface MarketCloses {
    market: ExportMarket;
    closes: ReadonlyMap<number, number>;
}

function priceTimes(series: readonly MarketCloses[]): number[] {
    const times = new Set<number>();
    for (const s of series) for (const t of s.closes.keys()) times.add(t);
    return [...times].sort((a, b) => a - b);
}

export function pricesCsv(series: readonly MarketCloses[]): string {
    const names = columnNames(series.map(s => s.market));
    const lines = [['timestamp', ...names].join(',')];
    for (const t of priceTimes(series)) {
        lines.push(
            [
                isoTime(t),
                ...series.map(s => {
                    const v = s.closes.get(t);
                    return v === undefined ? '' : price(v);
                }),
            ].join(',')
        );
    }
    return lines.join('\n') + '\n';
}

export function pricesJson(
    settings: ExportSettings,
    window: TimeWindow,
    series: readonly MarketCloses[]
): string {
    const names = columnNames(series.map(s => s.market));
    return JSON.stringify({
        ...headerOf(settings, window),
        outcomes: series.map((s, i) => ({
            column: names[i],
            condition_id: s.market.conditionId,
            outcome: s.market.label,
        })),
        prices: priceTimes(series).map(t => {
            const row: Record<string, string | number | null> = {
                timestamp: isoTime(t),
            };
            series.forEach((s, i) => {
                row[names[i]] = s.closes.get(t) ?? null;
            });
            return row;
        }),
    });
}

export function intervalToken(intervalMinutes: number): string {
    return INTERVAL_TOKENS[intervalMinutes] ?? `${intervalMinutes}min`;
}

function dateToken(ms: number, withTime: boolean): string {
    const iso = new Date(ms).toISOString();
    return withTime
        ? `${iso.slice(0, 10)}T${iso.slice(11, 13)}${iso.slice(14, 16)}`
        : iso.slice(0, 10);
}

export function rangeToken(window: TimeWindow): string {
    const day = 86_400_000;
    const withTime = window.startMs % day !== 0 || window.endMs % day !== 0;
    return withTime
        ? `${dateToken(window.startMs, true)}_${dateToken(window.endMs, true)}`
        : `${dateToken(window.startMs, false)}_${dateToken(window.endMs - 1, false)}`;
}

function tail(settings: ExportSettings, window: TimeWindow): string {
    return `${settings.side}_${intervalToken(settings.intervalMinutes)}_${rangeToken(window)}`;
}

export function marketFileName(
    settings: ExportSettings,
    window: TimeWindow,
    market: ExportMarket
): string {
    return `${market.conditionId}_${slugify(market.label)}_${tail(settings, window)}.${settings.file}`;
}

function eventToken(settings: ExportSettings): string {
    return slugify(settings.eventSlug || settings.eventTitle || 'event');
}

export function zipFileName(
    settings: ExportSettings,
    window: TimeWindow
): string {
    return `${eventToken(settings)}_candles_${tail(settings, window)}.zip`;
}

export function pricesFileName(
    settings: ExportSettings,
    window: TimeWindow
): string {
    return `${eventToken(settings)}_prices_${tail(settings, window)}.${settings.file}`;
}

export function infoJson(
    settings: ExportSettings,
    window: TimeWindow,
    files: readonly { name: string; market: ExportMarket; rows: number }[]
): string {
    return JSON.stringify(
        {
            ...headerOf(settings, window),
            data: settings.data,
            fields: ['timestamp', ...PRICE_FIELDS, ...fieldsFor(settings.side)],
            files: files.map(f => ({
                file: f.name,
                condition_id: f.market.conditionId,
                outcome: f.market.label,
                rows: f.rows,
            })),
        },
        null,
        2
    );
}

export function eventFolder(event: ExportEventRef): string {
    return slugify(event.slug || event.title || event.id || 'event');
}

export function selectionZipFileName(
    settings: ExportSettings,
    window: TimeWindow,
    events: number
): string {
    return `selection_${events}-events_${settings.data}_${tail(settings, window)}.zip`;
}

export interface SelectionFile {
    name: string;
    rows: number;
    window: TimeWindow;
    markets: readonly ExportMarket[];
}

export interface SelectionGroup {
    event: ExportEventRef;
    folder: string;
    files: readonly SelectionFile[];
}

export function selectionInfoJson(
    settings: ExportSettings,
    window: TimeWindow,
    groups: readonly SelectionGroup[]
): string {
    return JSON.stringify(
        {
            side: settings.side,
            interval_minutes: settings.intervalMinutes,
            from: isoTime(window.startMs),
            to: isoTime(window.endMs),
            filled: settings.fill,
            data: settings.data,
            ...(settings.data === 'candles'
                ? {
                      fields: [
                          'timestamp',
                          ...PRICE_FIELDS,
                          ...fieldsFor(settings.side),
                      ],
                  }
                : {}),
            events: groups.map(g => ({
                event_id: g.event.id,
                event_slug: g.event.slug,
                event_title: g.event.title,
                folder: g.folder,
                files: g.files.map(f => ({
                    file: f.name,
                    rows: f.rows,
                    from: isoTime(f.window.startMs),
                    to: isoTime(f.window.endMs),
                    outcomes: f.markets.map(m => ({
                        condition_id: m.conditionId,
                        outcome: m.label,
                    })),
                })),
            })),
        },
        null,
        2
    );
}
